import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuth } from '@/components/AuthProvider';

type AppFlowScreen = 'none' | 'launch' | 'profiles' | 'loading';

interface AppFlowContextType {
  /** Current overlay screen being shown */
  currentScreen: AppFlowScreen;
  /** Show the launch experience (video + profiles) */
  showLaunch: () => void;
  /** Show profile selection (skip video) */
  showProfiles: () => void;
  /** Show loading screen */
  showLoading: () => void;
  /** Close all overlays */
  closeOverlay: () => void;
  /** Mark profile selection as complete */
  completeProfileSelection: () => void;
  /** Whether profile selection has been completed this session */
  profileSelectionComplete: boolean;
}

const AppFlowContext = createContext<AppFlowContextType | undefined>(undefined);

interface AppFlowProviderProps {
  children: React.ReactNode;
}

export function AppFlowProvider({ children }: AppFlowProviderProps) {
  const { session, isLoading: authLoading } = useAuth();
  const [currentScreen, setCurrentScreen] = useState<AppFlowScreen>('none');
  const [profileSelectionComplete, setProfileSelectionComplete] = useState(() => {
    return sessionStorage.getItem('profile-selection-shown') === 'true';
  });

  // Check if we need to show launch/profiles on mount for authenticated users
  useEffect(() => {
    if (authLoading) return;
    
    if (session && !profileSelectionComplete) {
      // User is authenticated but hasn't selected a profile this session
      // Check if we should skip the video (returning user vs new session)
      const hasSeenLaunchBefore = localStorage.getItem('has-seen-launch-video') === 'true';
      
      if (hasSeenLaunchBefore) {
        setCurrentScreen('profiles');
      } else {
        setCurrentScreen('launch');
      }
    }
  }, [session, authLoading, profileSelectionComplete]);

  // Listen for sessionStorage changes
  useEffect(() => {
    const checkProfileSelection = () => {
      const shown = sessionStorage.getItem('profile-selection-shown') === 'true';
      if (shown && !profileSelectionComplete) {
        setProfileSelectionComplete(true);
        setCurrentScreen('none');
      }
    };

    window.addEventListener('storage', checkProfileSelection);
    return () => window.removeEventListener('storage', checkProfileSelection);
  }, [profileSelectionComplete]);

  const showLaunch = useCallback(() => {
    setCurrentScreen('launch');
  }, []);

  const showProfiles = useCallback(() => {
    setCurrentScreen('profiles');
  }, []);

  const showLoading = useCallback(() => {
    setCurrentScreen('loading');
  }, []);

  const closeOverlay = useCallback(() => {
    setCurrentScreen('none');
  }, []);

  const completeProfileSelection = useCallback(() => {
    sessionStorage.setItem('profile-selection-shown', 'true');
    localStorage.setItem('has-seen-launch-video', 'true');
    setProfileSelectionComplete(true);
    setCurrentScreen('none');
  }, []);

  return (
    <AppFlowContext.Provider 
      value={{ 
        currentScreen,
        showLaunch,
        showProfiles,
        showLoading,
        closeOverlay,
        completeProfileSelection,
        profileSelectionComplete
      }}
    >
      {children}
    </AppFlowContext.Provider>
  );
}

export function useAppFlow(): AppFlowContextType {
  const context = useContext(AppFlowContext);
  
  // During SSR or if called too early, return safe defaults
  // This prevents crashes during the initial render cycle
  if (context === undefined) {
    return {
      currentScreen: 'none',
      showLaunch: () => console.warn('useAppFlow: Provider not mounted yet'),
      showProfiles: () => console.warn('useAppFlow: Provider not mounted yet'),
      showLoading: () => console.warn('useAppFlow: Provider not mounted yet'),
      closeOverlay: () => console.warn('useAppFlow: Provider not mounted yet'),
      completeProfileSelection: () => console.warn('useAppFlow: Provider not mounted yet'),
      profileSelectionComplete: sessionStorage.getItem('profile-selection-shown') === 'true'
    };
  }
  
  return context;
}
